import {
  DESIRED_COLOR_OPTIONS,
  MATERIAL_OPTIONS,
  type TYPE_DESIRED_COLOR_OPTIONS,
  type TYPE_MATERIAL_OPTIONS,
} from './colors-and-materials';
import { ROOM_MODE_OPTIONS, type RoomMode } from './room-mode';
import { ROOM_TYPE_OPTIONS, type TYPE_ROOM_TYPE_OPTIONS } from './room-type';

// Room-specific color mappings
export const ROOM_COLOR_MAPPINGS: Record<TYPE_ROOM_TYPE_OPTIONS, TYPE_DESIRED_COLOR_OPTIONS[]> = {
  [ROOM_TYPE_OPTIONS.LIVING_ROOM]: [
    DESIRED_COLOR_OPTIONS.IVORY,
    DESIRED_COLOR_OPTIONS.WHITE,
    DESIRED_COLOR_OPTIONS.MINK,
    DESIRED_COLOR_OPTIONS.EARTH_TONES,
    DESIRED_COLOR_OPTIONS.WARM_SHADES,
    DESIRED_COLOR_OPTIONS.RICH_BLUES,
    DESIRED_COLOR_OPTIONS.DEEP_GREEN,
    DESIRED_COLOR_OPTIONS.BURGUNDY,
    DESIRED_COLOR_OPTIONS.GREY,
  ],
  [ROOM_TYPE_OPTIONS.BEDROOM]: [
    DESIRED_COLOR_OPTIONS.SOFT_WHITES,
    DESIRED_COLOR_OPTIONS.TAUPE,
    DESIRED_COLOR_OPTIONS.PASTEL_BLUE,
    DESIRED_COLOR_OPTIONS.SAGE_GREEN,
    DESIRED_COLOR_OPTIONS.BLUSH_PINK,
    DESIRED_COLOR_OPTIONS.WARM_GREY,
    DESIRED_COLOR_OPTIONS.BEIGE,
    DESIRED_COLOR_OPTIONS.MUTED_LAVENDER,
    DESIRED_COLOR_OPTIONS.CHARCOAL,
    DESIRED_COLOR_OPTIONS.NAVY,
  ],
  [ROOM_TYPE_OPTIONS.KITCHEN]: [
    DESIRED_COLOR_OPTIONS.WHITE,
    DESIRED_COLOR_OPTIONS.BLACK,
    DESIRED_COLOR_OPTIONS.GREY,
    DESIRED_COLOR_OPTIONS.STONE,
    DESIRED_COLOR_OPTIONS.EMERALD_GREEN,
    DESIRED_COLOR_OPTIONS.NAVY_BLUE,
    DESIRED_COLOR_OPTIONS.BRICK_RED,
    DESIRED_COLOR_OPTIONS.STEEL,
    DESIRED_COLOR_OPTIONS.TAUPE,
    DESIRED_COLOR_OPTIONS.SANDY_BEIGE,
  ],
  [ROOM_TYPE_OPTIONS.DINING_ROOM]: [
    DESIRED_COLOR_OPTIONS.IVORY,
    DESIRED_COLOR_OPTIONS.DARK_BROWN,
    DESIRED_COLOR_OPTIONS.RICH_BURGUNDY,
    DESIRED_COLOR_OPTIONS.NAVY_BLUE,
    DESIRED_COLOR_OPTIONS.OLIVE_GREEN,
    DESIRED_COLOR_OPTIONS.GOLD,
    DESIRED_COLOR_OPTIONS.MUTED_YELLOW,
    DESIRED_COLOR_OPTIONS.WHITE,
    DESIRED_COLOR_OPTIONS.GREY,
    DESIRED_COLOR_OPTIONS.WINE_RED,
  ],
  [ROOM_TYPE_OPTIONS.KITCHEN_LIVING_COMBO]: [
    DESIRED_COLOR_OPTIONS.EARTH_TONES,
    DESIRED_COLOR_OPTIONS.WHITE,
    DESIRED_COLOR_OPTIONS.GREY,
    DESIRED_COLOR_OPTIONS.NAVY,
    DESIRED_COLOR_OPTIONS.SOFT_GREEN,
    DESIRED_COLOR_OPTIONS.TAN,
    DESIRED_COLOR_OPTIONS.OLIVE,
    DESIRED_COLOR_OPTIONS.MINK,
    DESIRED_COLOR_OPTIONS.PASTEL_ACCENTS,
    DESIRED_COLOR_OPTIONS.CHARCOAL,
    DESIRED_COLOR_OPTIONS.BRICK_RED,
  ],
  [ROOM_TYPE_OPTIONS.BATHROOM]: [
    DESIRED_COLOR_OPTIONS.BLUE,
    DESIRED_COLOR_OPTIONS.GREEN,
    DESIRED_COLOR_OPTIONS.WHITE,
    DESIRED_COLOR_OPTIONS.SOFT_GREY,
    DESIRED_COLOR_OPTIONS.BEIGE,
    DESIRED_COLOR_OPTIONS.AQUA,
    DESIRED_COLOR_OPTIONS.PEBBLE_TAUPE,
    DESIRED_COLOR_OPTIONS.BLUSH,
    DESIRED_COLOR_OPTIONS.CHARCOAL,
    DESIRED_COLOR_OPTIONS.MINT,
  ],
  [ROOM_TYPE_OPTIONS.HOME_OFFICE]: [
    DESIRED_COLOR_OPTIONS.WHITE,
    DESIRED_COLOR_OPTIONS.LIGHT_WOOD,
    DESIRED_COLOR_OPTIONS.GREY,
    DESIRED_COLOR_OPTIONS.NAVY_BLUE,
    DESIRED_COLOR_OPTIONS.MINT_GREEN,
    DESIRED_COLOR_OPTIONS.NATURAL_BROWN,
    DESIRED_COLOR_OPTIONS.BLACK,
    DESIRED_COLOR_OPTIONS.TAUPE,
    DESIRED_COLOR_OPTIONS.WALNUT,
    DESIRED_COLOR_OPTIONS.CHARCOAL,
  ],

  [ROOM_TYPE_OPTIONS.OFFICE]: [
    DESIRED_COLOR_OPTIONS.WHITE,
    DESIRED_COLOR_OPTIONS.GREY,
    DESIRED_COLOR_OPTIONS.BLACK,
    DESIRED_COLOR_OPTIONS.TAUPE,
    DESIRED_COLOR_OPTIONS.WALNUT_BROWN,
    DESIRED_COLOR_OPTIONS.STEEL,
    DESIRED_COLOR_OPTIONS.LIGHT_BLUE,
    DESIRED_COLOR_OPTIONS.CHARCOAL,
    DESIRED_COLOR_OPTIONS.CHERRY,
    DESIRED_COLOR_OPTIONS.NATURAL_WOOD,
  ],

  [ROOM_TYPE_OPTIONS.MEETING_ROOM]: [
    DESIRED_COLOR_OPTIONS.WHITE,
    DESIRED_COLOR_OPTIONS.NATURAL_WOOD,
    DESIRED_COLOR_OPTIONS.CHARCOAL,
    DESIRED_COLOR_OPTIONS.NAVY,
    DESIRED_COLOR_OPTIONS.DARK_GREEN,
    DESIRED_COLOR_OPTIONS.LIGHT_GREY,
    DESIRED_COLOR_OPTIONS.DEEP_BROWN,
    DESIRED_COLOR_OPTIONS.BEIGE,
    DESIRED_COLOR_OPTIONS.GLASS_CLEAR,
    DESIRED_COLOR_OPTIONS.STEEL,
  ],

  [ROOM_TYPE_OPTIONS.COWORKING_SPACE]: [
    DESIRED_COLOR_OPTIONS.WHITE,
    DESIRED_COLOR_OPTIONS.BRIGHT_BLUE,
    DESIRED_COLOR_OPTIONS.ORANGE,
    DESIRED_COLOR_OPTIONS.ECO_GREEN,
    DESIRED_COLOR_OPTIONS.STEEL,
    DESIRED_COLOR_OPTIONS.NATURAL_BROWN,
    DESIRED_COLOR_OPTIONS.CHARCOAL,
    DESIRED_COLOR_OPTIONS.BEIGE,
    DESIRED_COLOR_OPTIONS.LIGHT_WOOD,
    DESIRED_COLOR_OPTIONS.SLATE,
  ],

  [ROOM_TYPE_OPTIONS.KIDS_ROOM]: [
    DESIRED_COLOR_OPTIONS.LIGHT_BLUE,
    DESIRED_COLOR_OPTIONS.SAGE_GREEN,
    DESIRED_COLOR_OPTIONS.BLUSH_PINK,
    DESIRED_COLOR_OPTIONS.WHITE,
    DESIRED_COLOR_OPTIONS.YELLOW,
    DESIRED_COLOR_OPTIONS.PASTEL_PURPLE,
    DESIRED_COLOR_OPTIONS.SOFT_GREY,
    DESIRED_COLOR_OPTIONS.MINT,
    DESIRED_COLOR_OPTIONS.TAUPE,
    DESIRED_COLOR_OPTIONS.PINE,
  ],

  [ROOM_TYPE_OPTIONS.WALK_IN_CLOSET]: [
    DESIRED_COLOR_OPTIONS.WHITE,
    DESIRED_COLOR_OPTIONS.LINEN,
    DESIRED_COLOR_OPTIONS.LIGHT_BROWN,
    DESIRED_COLOR_OPTIONS.MATTE_BLACK,
    DESIRED_COLOR_OPTIONS.GREY,
    DESIRED_COLOR_OPTIONS.CHARCOAL,
    DESIRED_COLOR_OPTIONS.WASHED_WOOD,
    DESIRED_COLOR_OPTIONS.GOLD,
    DESIRED_COLOR_OPTIONS.BRONZE,
    DESIRED_COLOR_OPTIONS.GLASS_CLEAR,
  ],
};

// Room-specific material mappings
export const ROOM_MATERIAL_MAPPINGS: Record<TYPE_ROOM_TYPE_OPTIONS, TYPE_MATERIAL_OPTIONS[]> = {
  [ROOM_TYPE_OPTIONS.LIVING_ROOM]: [
    MATERIAL_OPTIONS.OAK_HARDWOOD,
    MATERIAL_OPTIONS.TEAK,
    MATERIAL_OPTIONS.WHITE_ASH,
    MATERIAL_OPTIONS.MARBLE,
    MATERIAL_OPTIONS.VENEERED_WOOD,
    MATERIAL_OPTIONS.PLUSH_FABRICS,
    MATERIAL_OPTIONS.GLASS,
    MATERIAL_OPTIONS.METAL,
    MATERIAL_OPTIONS.DECORATIVE_MELAMINE,
    MATERIAL_OPTIONS.BWP_PLYWOOD,
    MATERIAL_OPTIONS.PORCELAIN_STONEWARE,
  ],
  [ROOM_TYPE_OPTIONS.BEDROOM]: [
    MATERIAL_OPTIONS.TEAK,
    MATERIAL_OPTIONS.OAK,
    MATERIAL_OPTIONS.FABRIC_UPHOLSTERY,
    MATERIAL_OPTIONS.LAMINATE,
    MATERIAL_OPTIONS.VENEER,
    MATERIAL_OPTIONS.PLUSH_TEXTILES,
    MATERIAL_OPTIONS.LACQUERED_WOOD,
    MATERIAL_OPTIONS.BWP_PLYWOOD,
    MATERIAL_OPTIONS.WHITE_ASH,
    MATERIAL_OPTIONS.BAMBOO,
    MATERIAL_OPTIONS.MDF,
  ],
  [ROOM_TYPE_OPTIONS.KITCHEN]: [
    MATERIAL_OPTIONS.TEAK,
    MATERIAL_OPTIONS.OAK,
    MATERIAL_OPTIONS.LAMINATE,
    MATERIAL_OPTIONS.MARBLE_COUNTERTOP,
    MATERIAL_OPTIONS.BWP_PLYWOOD,
    MATERIAL_OPTIONS.PORCELAIN_STONEWARE,
    MATERIAL_OPTIONS.STAINLESS_STEEL,
    MATERIAL_OPTIONS.GLASS,
    MATERIAL_OPTIONS.GRANITE,
    MATERIAL_OPTIONS.MDF,
    MATERIAL_OPTIONS.QUARTZ,
  ],
  [ROOM_TYPE_OPTIONS.DINING_ROOM]: [
    MATERIAL_OPTIONS.OAK,
    MATERIAL_OPTIONS.TEAK,
    MATERIAL_OPTIONS.MARBLE,
    MATERIAL_OPTIONS.GLASS,
    MATERIAL_OPTIONS.VENEER,
    MATERIAL_OPTIONS.PLUSH_FABRICS,
    MATERIAL_OPTIONS.METAL,
    MATERIAL_OPTIONS.LACQUERED_WOOD,
    MATERIAL_OPTIONS.PORCELAIN_STONEWARE,
    MATERIAL_OPTIONS.PORCELAIN_STONEWARE,
  ],
  [ROOM_TYPE_OPTIONS.KITCHEN_LIVING_COMBO]: [
    MATERIAL_OPTIONS.OAK,
    MATERIAL_OPTIONS.MARBLE,
    MATERIAL_OPTIONS.STAINLESS_STEEL,
    MATERIAL_OPTIONS.GLASS,
    MATERIAL_OPTIONS.DECORATIVE_MELAMINE,
    MATERIAL_OPTIONS.PORCELAIN_STONEWARE,
    MATERIAL_OPTIONS.LAMINATE,
    MATERIAL_OPTIONS.BWP_PLYWOOD,
    MATERIAL_OPTIONS.PLUSH_FABRICS_ACCENTCHAIRS,
    MATERIAL_OPTIONS.METAL,
    MATERIAL_OPTIONS.WHITE_ASH,
  ],
  [ROOM_TYPE_OPTIONS.BATHROOM]: [
    MATERIAL_OPTIONS.MOSAIC_TILES,
    MATERIAL_OPTIONS.FISHBONE_TILES,
    MATERIAL_OPTIONS.BWP_PLYWOOD,
    MATERIAL_OPTIONS.MARBLE,
    MATERIAL_OPTIONS.GLASS,
    MATERIAL_OPTIONS.PORCELAIN_STONEWARE,
    MATERIAL_OPTIONS.LAMINATE,
    MATERIAL_OPTIONS.GRANITE,
    MATERIAL_OPTIONS.STAINLESS_STEEL,
    MATERIAL_OPTIONS.CERAMIC,
    MATERIAL_OPTIONS.MDF,
  ],
  [ROOM_TYPE_OPTIONS.HOME_OFFICE]: [
    MATERIAL_OPTIONS.MAPLE_WOOD,
    MATERIAL_OPTIONS.OAK_HARDWOOD,
    MATERIAL_OPTIONS.CHERRY_WOOD,
    MATERIAL_OPTIONS.WALNUT_WOOD,
    MATERIAL_OPTIONS.METAL,
    MATERIAL_OPTIONS.LAMINATE,
    MATERIAL_OPTIONS.GLASS,
    MATERIAL_OPTIONS.BAMBOO,
    MATERIAL_OPTIONS.MDF,
    MATERIAL_OPTIONS.BWP_PLYWOOD,
  ],
  [ROOM_TYPE_OPTIONS.OFFICE]: [
    MATERIAL_OPTIONS.OAK_HARDWOOD,
    MATERIAL_OPTIONS.MAPLE_WOOD,
    MATERIAL_OPTIONS.CHERRY_WOOD,
    MATERIAL_OPTIONS.WALNUT_WOOD,
    MATERIAL_OPTIONS.METAL,
    MATERIAL_OPTIONS.LAMINATE,
    MATERIAL_OPTIONS.GLASS,
    MATERIAL_OPTIONS.HDPE,
    MATERIAL_OPTIONS.VENEER,
    MATERIAL_OPTIONS.ENGINEERED_WOOD,
  ],
  [ROOM_TYPE_OPTIONS.MEETING_ROOM]: [
    MATERIAL_OPTIONS.SOLID_WOOD,
    MATERIAL_OPTIONS.VENEER,
    MATERIAL_OPTIONS.LAMINATE,
    MATERIAL_OPTIONS.GLASS,
    MATERIAL_OPTIONS.METAL,
    MATERIAL_OPTIONS.STONE,
    MATERIAL_OPTIONS.BAMBOO,
    MATERIAL_OPTIONS.MDF,
    MATERIAL_OPTIONS.BWP_PLYWOOD,
    MATERIAL_OPTIONS.ACRYLIC,
  ],
  [ROOM_TYPE_OPTIONS.COWORKING_SPACE]: [
    MATERIAL_OPTIONS.OAK_HARDWOOD,
    MATERIAL_OPTIONS.BAMBOO,
    MATERIAL_OPTIONS.METAL,
    MATERIAL_OPTIONS.LAMINATE,
    MATERIAL_OPTIONS.GLASS,
    MATERIAL_OPTIONS.ENGINEERED_WOOD,
    MATERIAL_OPTIONS.RECYCLED_PLASTICS,
    MATERIAL_OPTIONS.FABRIC,
    MATERIAL_OPTIONS.LEATHER,
    MATERIAL_OPTIONS.HDPE,
  ],
  [ROOM_TYPE_OPTIONS.KIDS_ROOM]: [
    MATERIAL_OPTIONS.SOLID_WOOD,
    MATERIAL_OPTIONS.BAMBOO,
    MATERIAL_OPTIONS.COTTON,
    MATERIAL_OPTIONS.LINEN,
    MATERIAL_OPTIONS.DENIM,
    MATERIAL_OPTIONS.MDF,
    MATERIAL_OPTIONS.BWP_PLYWOOD,
    MATERIAL_OPTIONS.MELAMINE,
    MATERIAL_OPTIONS.RECYCLED_PLASTIC,
    MATERIAL_OPTIONS.SAFE_PAINTS,
  ],
  [ROOM_TYPE_OPTIONS.WALK_IN_CLOSET]: [
    MATERIAL_OPTIONS.SOLID_WOOD,
    MATERIAL_OPTIONS.MDF,
    MATERIAL_OPTIONS.MELAMINE,
    MATERIAL_OPTIONS.TEMPERED_GLASS,
    MATERIAL_OPTIONS.METAL,
    MATERIAL_OPTIONS.RECYCLED_PLASTIC,
    MATERIAL_OPTIONS.MARBLE,
    MATERIAL_OPTIONS.VENEER,
    MATERIAL_OPTIONS.LACQUERED_FINISHES,
    MATERIAL_OPTIONS.MARINE_GRADE_PLYWOOD,
  ],
};

export const CHECKBOXES_OPTIONS = {
  KEEP_FLOOR: {
    value: 'keepFloor',
    description: 'Keep the floor material or design',
    checked: false,
    editable: true,
  },
  // KEEP_WALL: {
  //   value: 'keepWall',
  //   description: 'Keep the wall material or design',
  //   checked: false,
  //   editable: true,
  // },
} as const;
type CHECKBOXES_OPTIONS = keyof typeof CHECKBOXES_OPTIONS;
export type TYPE_CHECKBOXES_OPTIONS = (typeof CHECKBOXES_OPTIONS)[CHECKBOXES_OPTIONS];

export interface CheckboxConfig {
  checked: boolean;
  editable: boolean;
}

export const FLOOR_AND_WALL_CHECKBOX_KEYS = ['keepFloor', 'keepWall'] as const;

export const ROOM_MODE_CHECKBOX_CONFIG: Record<RoomMode, CheckboxConfig> = {
  [ROOM_MODE_OPTIONS.FURNISH_EMPTY_ROOM.value]: {
    checked: true,
    editable: false,
  },
  [ROOM_MODE_OPTIONS.CHANGE_STYLE.value]: {
    checked: true,
    editable: true,
  },
  [ROOM_MODE_OPTIONS.FURNITURE_REMOVAL.value]: {
    checked: false,
    editable: false,
  },
  [ROOM_MODE_OPTIONS.FINISH_CONSTRUCTION.value]: {
    checked: false,
    editable: true,
  },
  [ROOM_MODE_OPTIONS.CHANGE_FLOOR.value]: {
    checked: false,
    editable: true,
  },
  [ROOM_MODE_OPTIONS.CLEAN_ROOM.value]: {
    checked: false,
    editable: false,
  },
  [ROOM_MODE_OPTIONS.IMPROVE_IMAGE_QUALITY.value]: {
    checked: false,
    editable: true,
  },
  [ROOM_MODE_OPTIONS.GOOD_WEATHER.value]: {
    checked: false,
    editable: false,
  },
  [ROOM_MODE_OPTIONS.ENHANCE_AND_UPSCALE.value]: {
    checked: false,
    editable: false,
  },
  [ROOM_MODE_OPTIONS.PHOTOREALISTIC_FROM_3D.value]: {
    checked: false,
    editable: false,
  },
};
